import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { FieldArray, getIn } from 'formik';
import _ from 'lodash';
import { BiPlus, BiTrash } from 'react-icons/bi';
import TextInput from 'components/inputs/Input';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import CheckBox from 'components/inputs/CheckBox';
import SessionHeading from 'components/general/SessionHeading';
import { ALL_DAYS, RATE_TIMING_TYPES, RATE_WINDOW_TYPES, WEEK_DAYS, distinctBaseLines, isEnergyLine, slotDays } from './rateWindowUtils';

// Day option values are strings because Select treats a falsy option value (0 = Sunday) as "no value"
const DAY_OPTIONS = _.map(WEEK_DAYS, (day) => ({ label: day.label, value: String(day.value) }));

const fieldError = (errors, touched, path) => (getIn(errors, path) && getIn(touched, path) ? getIn(errors, path) : null);

const errorStyle = { color: 'red', fontSize: 11 };
const cellStyle = { verticalAlign: 'top', minWidth: 130 };
const pluCellStyle = { verticalAlign: 'top', minWidth: 170 };

const emptySlot = (days) => ({ type: '', days: days || [], from: '', to: '', lines: [] });


// "Dynamic Timing (Peak / Off-Peak)": a timing type plus slot rows. Each slot maps to one rateWindows entry on submit.
// Production has no PLU catalogue, so each slot line takes its PLU, name and price as typed values
// (the same way the tariff lines above are entered).
const RateWindowsSection = ({ values, errors, touched, setFieldValue }) => {
  const timingType = values.rateTimingType || 'none';
  const isCustom = timingType === 'custom';
  const baseLines = distinctBaseLines(values.tariffs);
  const sectionError = typeof getIn(errors, 'rateWindows') === 'string' ? getIn(errors, 'rateWindows') : null;

  const setLineField = (slotIndex, basePlu, field, val) => {
    const currentLines = _.get(values, `rateWindows[${slotIndex}].lines`) || [];
    const existing = _.find(currentLines, (line) => line.basePlu === basePlu) || { basePlu, plu: '', pluNameEn: '', unitPrice: '' };
    const otherLines = _.filter(currentLines, (line) => line.basePlu !== basePlu);
    const updated = { ...existing, [field]: val };
    const isEmpty = _.every(['plu', 'pluNameEn', 'unitPrice'], (key) => _.isNil(updated[key]) || updated[key] === '');
    setFieldValue(`rateWindows[${slotIndex}].lines`, isEmpty ? otherLines : [...otherLines, updated]);
  };

  const onTimingTypeChange = (val) => {
    const next = val || 'none';
    const slots = values.rateWindows || [];
    if (next === 'custom') {
      // Carry the previous preset's days into each slot so switching to Custom keeps the same schedule
      setFieldValue(
        'rateWindows',
        _.map(slots, (slot) => ({ ...slot, days: _.isEmpty(slot.days) || timingType !== 'custom' ? [...slotDays(timingType, slot)] : slot.days }))
      );
    } else if (next !== 'none' && _.isEmpty(slots)) {
      setFieldValue('rateWindows', [emptySlot()]);
    }
    setFieldValue('rateTimingType', next);
  };

  return (
    <>
      <SessionHeading text="Dynamic Timing (Peak / Off-Peak)" />
      <div className="pricing-form-page__main">
        <div className="schedule-block__field-inputs">
          <Row>
            <Col lg={4}>
              <Select
                isRequired
                label="Timing Type"
                options={RATE_TIMING_TYPES}
                sortOptions={false}
                placeholder="Select Timing Type"
                name="rateTimingType"
                value={timingType}
                onChange={onTimingTypeChange}
                error={fieldError(errors, touched, 'rateTimingType')}
              />
            </Col>
          </Row>
          {timingType !== 'none' && (
            <>
              <p className="mb-2" style={{ fontSize: 13 }}>
                Any time not covered by a slot is charged at the tariff&apos;s normal price above. Times are UAE local time; a slot whose To is
                earlier than or equal to its From runs past midnight into the next day (use 00:00 as To for &quot;until midnight&quot;).
              </p>
              <p className="mb-2" style={{ fontSize: 13 }}>
                Type (Peak / Off-Peak) is a label for reports, the dashboard and the charger display. The price charged in a slot comes from the PLU
                selected for that slot.
              </p>
              {_.isEmpty(baseLines) && (
                <div className="mb-2" style={{ fontSize: 13 }}>
                  Enter a PLU on the tariff lines above to set slot prices.
                </div>
              )}
              <FieldArray
                name="rateWindows"
                render={(arrayHelpers) => (
                  <>
                    <div className="table-responsive">
                      <table className="record-list-table">
                        <thead>
                          <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Type</th>
                            {isCustom && <th>Days</th>}
                            {_.isEmpty(baseLines) && (
                              <>
                                <th>
                                  PLU<span className="require--star">*</span>
                                </th>
                                <th>Price</th>
                              </>
                            )}
                            {_.map(baseLines, (baseLine) => (
                              <React.Fragment key={`slot-head-${baseLine.plu}`}>
                                <th>
                                  {`PLU for ${baseLine.plu}${baseLine.unit ? ` (${baseLine.unit})` : ''}`}
                                  {isEnergyLine(baseLine) && <span className="require--star">*</span>}
                                </th>
                                <th>Price</th>
                              </React.Fragment>
                            ))}
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {_.map(values.rateWindows, (slot, index) => {
                            const slotDayValues = _.map(slot.days, String);
                            const allDaysSelected = ALL_DAYS.every((day) => slotDayValues.includes(String(day)));
                            const linesError = fieldError(errors, touched, `rateWindows[${index}].lines`);
                            const runsPastMidnight = slot.from && slot.to && slot.to <= slot.from;
                            return (
                              <tr key={`rateWindows-${index}`}>
                                <td style={cellStyle}>
                                  <TextInput
                                    type="time"
                                    name="from"
                                    value={slot.from || ''}
                                    onChange={(e) => setFieldValue(`rateWindows[${index}].from`, e.target.value)}
                                    error={fieldError(errors, touched, `rateWindows[${index}].from`)}
                                  />
                                </td>
                                <td style={cellStyle}>
                                  <TextInput
                                    type="time"
                                    name="to"
                                    value={slot.to || ''}
                                    onChange={(e) => setFieldValue(`rateWindows[${index}].to`, e.target.value)}
                                    error={fieldError(errors, touched, `rateWindows[${index}].to`)}
                                  />
                                  {runsPastMidnight && <div style={{ fontSize: 11 }}>Runs past midnight (next day)</div>}
                                </td>
                                <td style={cellStyle}>
                                  <Select
                                    options={RATE_WINDOW_TYPES}
                                    sortOptions={false}
                                    placeholder="Type"
                                    name="type"
                                    value={slot.type || ''}
                                    onChange={(val) => setFieldValue(`rateWindows[${index}].type`, val || '')}
                                    error={fieldError(errors, touched, `rateWindows[${index}].type`)}
                                  />
                                </td>
                                {isCustom && (
                                  <td style={{ verticalAlign: 'top', minWidth: 240 }}>
                                    <Select
                                      isMulti
                                      options={DAY_OPTIONS}
                                      sortOptions={false}
                                      placeholder="Select Days"
                                      name="days"
                                      value={slotDayValues}
                                      onChange={(val) =>
                                        setFieldValue(`rateWindows[${index}].days`, _.sortBy(_.map(val || [], (day) => parseInt(day, 10))))
                                      }
                                      error={fieldError(errors, touched, `rateWindows[${index}].days`)}
                                    />
                                    <CheckBox
                                      id={`rateWindows-${index}-allDays`}
                                      name="allDays"
                                      checked={allDaysSelected}
                                      onChange={(e) => setFieldValue(`rateWindows[${index}].days`, e.target.checked ? [...ALL_DAYS] : [])}
                                    >
                                      All days
                                    </CheckBox>
                                  </td>
                                )}
                                {_.isEmpty(baseLines) && (
                                  <>
                                    <td style={pluCellStyle}>
                                      <TextInput disabled placeholder="Enter tariff PLU first" name="plu" value="" onChange={_.noop} />
                                    </td>
                                    <td style={{ verticalAlign: 'top', minWidth: 90 }}>-</td>
                                  </>
                                )}
                                {_.map(baseLines, (baseLine, lineIndex) => {
                                  const override = _.find(slot.lines, (line) => line.basePlu === baseLine.plu);
                                  return (
                                    <React.Fragment key={`rateWindows-${index}-line-${baseLine.plu}`}>
                                      <td style={pluCellStyle}>
                                        <TextInput
                                          placeholder={isEnergyLine(baseLine) ? 'PLU' : 'PLU (blank = normal price)'}
                                          name="plu"
                                          value={_.get(override, 'plu', '')}
                                          onChange={(e) => setLineField(index, baseLine.plu, 'plu', _.trim(e.target.value))}
                                        />
                                        <TextInput
                                          placeholder="PLU name (EN)"
                                          name="pluNameEn"
                                          value={_.get(override, 'pluNameEn', '')}
                                          onChange={(e) => setLineField(index, baseLine.plu, 'pluNameEn', e.target.value)}
                                        />
                                        {lineIndex === 0 && linesError && <div style={errorStyle}>{linesError}</div>}
                                      </td>
                                      <td style={{ verticalAlign: 'top', minWidth: 110 }}>
                                        <TextInput
                                          type="number"
                                          min="0"
                                          step="any"
                                          placeholder="Price"
                                          name="unitPrice"
                                          value={_.get(override, 'unitPrice', '')}
                                          onChange={(e) => setLineField(index, baseLine.plu, 'unitPrice', e.target.value)}
                                        />
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                <td style={{ verticalAlign: 'top' }}>
                                  <Button className="field--btn" onClick={() => arrayHelpers.remove(index)}>
                                    <BiTrash size={22} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {sectionError && <div style={{ color: 'red', fontSize: 12 }}>{sectionError}</div>}
                    <div>
                      <Button
                        className="field--btn"
                        onClick={() => arrayHelpers.insert(_.size(values.rateWindows), emptySlot(isCustom ? [...ALL_DAYS] : []))}
                      >
                        <BiPlus size={22} className="plus--icon" /> Add Slot
                      </Button>
                    </div>
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

RateWindowsSection.propTypes = {
  values: PropTypes.shape({
    tariffs: PropTypes.array,
    rateWindows: PropTypes.array,
    rateTimingType: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
};

export default RateWindowsSection;
